// @flow
import { OrderedMap } from 'immutable';
import update from 'immutability-helper';

type DB = {
  [key: string]: {
    // items?: any[],
    canAsk: boolean;
    page: number;
    cache?: OrderedMap<string, any>;
  };
};

const PAGE_LIMIT = 12;
const INITIAL_PAGE = 0;

declare function CustomFunction(orderedMap: OrderedMap<string, any>, ...args: any[]): OrderedMap<string, any>;

// Paginator class: it handles the income of the items to the list by using a cache
// (OrderedMap, because we want to have unique objects, ordered by insertion and indexed by
// some criteria) and exporting it by the use of `getItems`.
// The way the paginator increments (add) new items is a generic function that append them to the
// previously saved items. It calculates the page.
export default class Paginator {
  db: DB = {};
  limit: number = PAGE_LIMIT;

  constructor(keys: string[], limit?: number) {
    keys.forEach((key: string) => this.db[key] = {
      canAsk: true,
      page: INITIAL_PAGE,
    });
    if (limit) this.limit = limit;
  }

  resetAll = () =>
    Object.keys(this.db).forEach((key: string) => this.reset(key));

  reset(key: string) {
    if (!this.db[key]) throw new Error('No key to reset');
    this.db[key] = update(this.db[key], {
      $unset: ['cache'],
      page: { $set: INITIAL_PAGE },
      canAsk: { $set: true },
    });
  }

  // Another experiment, this time is for just increment in custom wae.=
  // This function receives the index key as `key` argument. `items` are the new items to be
  // added. `currentItems` are the previous saved items.
  increment(key: string, items?: any[] = [], currentItems?: any[], indexKey?: string = 'id') {
    // If cannot ask, exits
    if (!this.db[key].canAsk) return;
    // Handle map cache init (happens only in pre-rendered content, it will have to recalculate)
    if (!this.db[key].cache) {
      // Only if currentItems is provided, happens if pre-rendered
      if (currentItems) {
        this.db[key].page = Math.ceil(currentItems.length / this.limit);
        this.db[key].cache = OrderedMap(currentItems.map(x => [x[indexKey], x]));
      } else {
        this.db[key].page = INITIAL_PAGE;
        this.db[key].cache = OrderedMap();
      }
    }
    // Handle map cache generic concat
    this.db[key].cache = this.db[key].cache.merge(items.map(x => [x[indexKey], x]));
    // Handle page
    this.db[key].page += 1;
    // Handle can ask
    this.db[key].canAsk = items.length !== 0 && items.length === this.limit;
  }

  // same as `increment` but it receives a custom function to handle
  // the merge between `items` and `currentItems`
  incrementCustom(key: string, items: ?any[], currentItems: ?any[], fn: CustomFunction) {
    // If cannot ask, exits
    if (!this.db[key].canAsk) return;
    // If not items, return
    if (!items) return;
    // Handle map cache init (happens only in pre-rendered content, it will have to recalculate)
    if (!this.db[key].cache) {
      // both cases initialize
      this.db[key].cache = OrderedMap();
      // Only if currentItems is provided, happens if pre-rendered
      if (currentItems) {
        this.db[key].page = Math.ceil(currentItems.length / this.limit);
        // Initialize with the same function
        this.db[key].cache = fn(this.db[key].cache, currentItems);
      } else {
        this.db[key].page = INITIAL_PAGE;
      }
    }
    // Handle map change with custom function
    this.db[key].cache = fn(this.db[key].cache, items);
    // Handle page
    this.db[key].page += 1;
    // Handle can ask
    this.db[key].canAsk = items.length !== 0 && items.length === this.limit;
  }

  // This custom operation thing is an experiment
  // I need to handle some custom changes inside the items
  // I don't think a mini-engine is necessary yet
  executeCustomOperation(key?: string, fn: CustomFunction) {
    if (key && this.db[key].cache) {
      this.db[key].cache = fn(this.db[key].cache);
    }
  }

  getRawCache(key: string): ?OrderedMap<string, any> {
    return this.db[key].cache;
  }

  getItems(key: string) {
    if (this.db[key].cache) {
      return this.db[key].cache.toList().toJS();
    }
  }

  getCanAsk(key: string): boolean {
    return this.db[key].canAsk;
  }

  getPage(key: string): number {
    return this.db[key].page;
  }

  getLimit(): number {
    return this.limit;
  }
}



// WEBPACK FOOTER //
// ./src/common/Paginator.js